import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Portal-based dropdown for the Model Hub toolbar, on the shared
 * labelflow-dropdown-* styles.
 *
 * options: [{ value, label, count?, hue? }]
 *
 * Four things in here are load-bearing, and each one is a way the owner
 * dropdown was silently unusable before:
 *
 *  - The menu is portalled to <body>, so it is NOT a DOM descendant of the
 *    trigger. An outside-press check that only asks `trigger.contains(target)`
 *    therefore counts a press on a menu item as "outside" and closes on
 *    mousedown — unmounting the item before mouseup, so the browser never
 *    dispatches a click and onChange never fires. Selecting an owner did
 *    nothing at all. The menu has to be exempted explicitly.
 *  - The listener that closes the menu on page scroll is registered with
 *    capture:true, so it also sees the menu's OWN scroll. With a scrollable
 *    list that means the menu shuts the moment you scroll it. Inner scrolls
 *    are ignored.
 *  - The list is capped and scrollable, and placement is viewport-aware: a
 *    long owner list opened near the bottom of the page flips above the
 *    trigger instead of running off-screen where it cannot be reached.
 *  - Keyboard: the trigger claims aria-haspopup="listbox", so arrows / Home /
 *    End / Enter / Escape have to actually work.
 */

const MENU_GAP = 6;
const MENU_MAX_H = 300;
const VIEWPORT_PAD = 10;
const SEARCH_AFTER = 8; // show the filter box once the list stops being scannable

const computePlacement = (triggerEl, minWidth) => {
  const rect = triggerEl.getBoundingClientRect();
  const vh = window.innerHeight || 0;
  const vw = window.innerWidth || 0;
  const below = vh - rect.bottom - MENU_GAP - VIEWPORT_PAD;
  const above = rect.top - MENU_GAP - VIEWPORT_PAD;
  const flip = below < Math.min(MENU_MAX_H, 200) && above > below;

  const width = Math.max(rect.width, minWidth);
  let left = rect.left;
  if (left + width > vw - VIEWPORT_PAD) left = rect.right - width;
  if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

  return {
    left,
    top: flip ? null : rect.bottom + MENU_GAP,
    bottom: flip ? Math.max(VIEWPORT_PAD, vh - rect.top + MENU_GAP) : null,
    width,
    maxHeight: Math.max(150, Math.min(MENU_MAX_H, flip ? above : below)),
  };
};

const HubDropdown = ({
  icon,
  label,
  value,
  options,
  onChange,
  minWidth = 200,
  searchable,
  searchPlaceholder = 'Search…',
  ariaLabel,
  active = false,
}) => {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const triggerRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  const uid = useId();
  const optionId = (v) => `${uid}-opt-${String(v).replace(/[^\w-]/g, '_')}`;

  const showSearch = searchable ?? options.length > SEARCH_AFTER;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label ?? '').toLowerCase().includes(q));
  }, [options, query]);

  const selected = options.find((o) => o.value === value) || null;

  const close = useCallback((restoreFocus) => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
    if (restoreFocus) btnRef.current?.focus();
  }, []);

  const openMenu = useCallback(
    (startIndex) => {
      if (!triggerRef.current) return;
      setPlacement(computePlacement(triggerRef.current, minWidth));
      setQuery('');
      setActiveIndex(
        typeof startIndex === 'number'
          ? startIndex
          : Math.max(0, options.findIndex((o) => o.value === value)),
      );
      setOpen(true);
    },
    [minWidth, options, value],
  );

  const commit = useCallback(
    (opt) => {
      if (!opt) return;
      onChange(opt.value);
      close(true);
    },
    [close, onChange],
  );

  // Outside press closes; a press inside the portalled menu must not.
  useEffect(() => {
    if (!open) return undefined;

    const inMenu = (node) => !!(node instanceof Node && menuRef.current?.contains(node));
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target) || inMenu(e.target)) return;
      close(false);
    };
    const onScroll = (e) => {
      if (inMenu(e.target)) return; // scrolling the list itself
      close(false);
    };
    const onResize = () => {
      if (triggerRef.current) setPlacement(computePlacement(triggerRef.current, minWidth));
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close, minWidth]);

  // Move focus into the menu so the keyboard path works at all.
  useEffect(() => {
    if (!open) return;
    const target = showSearch ? searchRef.current : listRef.current;
    target?.focus?.({ preventScroll: true });
  }, [open, showSearch]);

  // Keep the highlighted option in view while arrowing through a long list.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  const move = (delta) => {
    if (!visible.length) return;
    setActiveIndex((i) => {
      if (i < 0) return delta > 0 ? 0 : visible.length - 1;
      return (i + delta + visible.length) % visible.length;
    });
  };

  // Enter/Space on the trigger already produce a click; only arrows need help.
  const onTriggerKeyDown = (e) => {
    if (open || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    e.preventDefault();
    openMenu(e.key === 'ArrowUp' ? options.length - 1 : undefined);
  };

  const onMenuKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(visible.length ? 0 : -1);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(visible.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        commit(visible[activeIndex]);
        break;
      default:
        break;
    }
  };

  return (
    <div className="labelflow-dropdown-trigger hub-dropdown" ref={triggerRef}>
      <button
        ref={btnRef}
        type="button"
        className={`hub-dropdown__btn${open ? ' is-open' : ''}${active ? ' is-filtered' : ''}`}
        onClick={() => (open ? close(true) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || label}
      >
        {icon && <i className={`fa ${icon}`} aria-hidden />}
        <span className="hub-dropdown__value">{selected ? selected.label : label}</span>
        <i className="fa fa-chevron-down hub-dropdown__caret" aria-hidden />
      </button>

      {open && placement &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="labelflow-dropdown-portal hub-dropdown-portal"
            style={{
              position: 'fixed',
              left: placement.left,
              ...(placement.top != null ? { top: placement.top } : { bottom: placement.bottom }),
              minWidth: placement.width,
              zIndex: 1060,
            }}
            onKeyDown={onMenuKeyDown}
          >
            <div className="labelflow-dropdown-menu hub-dropdown__menu">
              {showSearch && (
                <div className="hub-dropdown__search">
                  <i className="fa fa-search" aria-hidden />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setActiveIndex(0);
                    }}
                  />
                </div>
              )}

              <div
                ref={listRef}
                className="hub-dropdown__list"
                role="listbox"
                tabIndex={-1}
                aria-label={ariaLabel || label}
                aria-activedescendant={
                  visible[activeIndex] ? optionId(visible[activeIndex].value) : undefined
                }
                style={{ maxHeight: placement.maxHeight }}
              >
                {visible.length === 0 ? (
                  <p className="hub-dropdown__none">No matches</p>
                ) : (
                  visible.map((opt, i) => {
                    const isSelected = opt.value === value;
                    return (
                      <button
                        key={String(opt.value)}
                        id={optionId(opt.value)}
                        data-idx={i}
                        type="button"
                        role="option"
                        tabIndex={-1}
                        aria-selected={isSelected}
                        className={`labelflow-dropdown-item hub-dropdown__item${
                          isSelected ? ' is-active' : ''
                        }${i === activeIndex ? ' is-focused' : ''}`}
                        onMouseEnter={() => setActiveIndex(i)}
                        onMouseDown={(e) => e.preventDefault()} // keep focus in the menu
                        onClick={() => commit(opt)}
                      >
                        <i
                          className={`fa fa-check hub-dropdown__tick${isSelected ? ' is-on' : ''}`}
                          aria-hidden
                        />
                        {opt.hue != null && (
                          <span
                            className="hub-dropdown__dot"
                            style={{ background: `hsl(${opt.hue} 55% 42%)` }}
                            aria-hidden
                          />
                        )}
                        <span className="hub-dropdown__item-label">{opt.label}</span>
                        {opt.count != null && (
                          <span className="hub-dropdown__count">{opt.count}</span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default HubDropdown;
